/**
 * Helper functions for working with Stream Chat data
 */

/**
 * Format a timestamp to a human-readable string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

/**
 * Generate a unique channel ID for direct messages
 */
export const generateDirectChannelId = (userId1, userId2) => {
  return [userId1, userId2].sort().join('-');
};

/**
 * Extract other members from a channel
 */
export const getOtherMembers = (channel, currentUserId) => {
  if (!channel?.state?.members) return [];
  
  return Object.values(channel.state.members)
    .filter(member => member.user.id !== currentUserId)
    .map(member => member.user);
};

/**
 * Get channel display name
 */
export const getChannelDisplayName = (channel, currentUserId) => {
  // If channel has a custom name, use it
  if (channel.data?.name) {
    return channel.data.name;
  }
  
  // For direct messages, use the other person's name
  const otherMembers = getOtherMembers(channel, currentUserId);
  if (otherMembers.length === 1) {
    return otherMembers[0].name || otherMembers[0].id;
  }
  
  // For group chats, list member names
  if (otherMembers.length > 1) {
    const names = otherMembers.slice(0, 2).map(m => m.name || m.id);
    if (otherMembers.length > 2) {
      names.push(`+${otherMembers.length - 2} more`);
    }
    return names.join(', ');
  }
  
  return 'Unknown';
};

/**
 * Get channel avatar(s)
 */
export const getChannelAvatars = (channel, currentUserId) => {
  const otherMembers = getOtherMembers(channel, currentUserId);
  
  if (otherMembers.length === 0) {
    return ['/images/content/avatar-1.jpg'];
  }
  
  return otherMembers
    .slice(0, 2)
    .map(member => member.image || '/images/content/avatar-1.jpg');
};

/**
 * Check if a channel has unread messages
 */
export const hasUnreadMessages = (channel, currentUserId) => {
  if (!channel?.state?.read) return false;
  
  const userRead = channel.state.read[currentUserId];
  if (!userRead) return true;
  
  const lastMessage = channel.state.messages[channel.state.messages.length - 1];
  if (!lastMessage) return false;
  
  return new Date(lastMessage.created_at) > new Date(userRead.last_read);
};

/**
 * Count unread messages in a channel
 */
export const countUnreadMessages = (channel, currentUserId) => {
  if (!channel?.state?.read || !channel?.state?.messages) return 0;
  
  const userRead = channel.state.read[currentUserId];
  if (!userRead) return channel.state.messages.length;
  
  const lastReadTime = new Date(userRead.last_read);
  
  return channel.state.messages.filter(
    msg => new Date(msg.created_at) > lastReadTime
  ).length;
};

/**
 * Get typing users text
 */
export const getTypingUsersText = (typingUsers, currentUserId) => {
  const typingOthers = typingUsers.filter(user => user.id !== currentUserId);
  
  if (typingOthers.length === 0) return null;
  if (typingOthers.length === 1) return `${typingOthers[0].name} is typing...`;
  if (typingOthers.length === 2) {
    return `${typingOthers[0].name} and ${typingOthers[1].name} are typing...`;
  }
  return `${typingOthers.length} people are typing...`;
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Check if a file is an image
 */
export const isImageFile = (file) => {
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return imageTypes.includes(file.type);
};

/**
 * Generate mock JWT token for development
 */
export const generateMockToken = (userId, email, userType) => {
  // This is only for development/testing
  // In production, tokens should always come from the server
  const payload = {
    userId,
    email,
    userType,
    name: email.split('@')[0],
    iat: Date.now() / 1000,
    exp: (Date.now() / 1000) + (24 * 60 * 60) // 24 hours
  };
  
  // This is a mock token - not cryptographically secure
  return btoa(JSON.stringify(payload));
};

/**
 * Clean handle by removing timestamp suffix (e.g., "username-1234567890" -> "username")
 * and stripping any leading "@" characters (prevents @@handle when display adds its own @)
 * @param {string} handle - The handle to clean
 * @returns {string} - Cleaned handle without timestamp or leading @
 */
export const cleanHandle = (handle) => {
  if (!handle) return handle;
  return handle.replace(/-\d+$/, '').replace(/^@+/, '');
};

/**
 * Format a handle for display with a single leading @.
 * Safely strips any existing @ prefixes before adding one.
 * @param {string} handle - Raw handle value (may or may not start with @)
 * @returns {string} - Handle formatted as "@handle", or empty string if no handle
 */
export const displayHandle = (handle) => {
  if (!handle) return '';
  const cleaned = cleanHandle(handle);
  return cleaned ? `@${cleaned}` : '';
};
